if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();

      this.stockPrioritySort = 'best-selling';

      this.productsPerPage = parseInt(
        this.dataset.productsPerPage || '16',
        10
      );

      this.stockPage = 1;
      this.outOfStockPage = 1;

      this.inStockFinished = false;
      this.outOfStockFinished = false;

      this.isFetching = false;
      this.controller = null;

      this.intersectingTimer = null;
      this.paginationObserver = null;

      /*
       * Store the exact URL/filter state that this
       * instance was initialised with.
       *
       * This is important because Shopify can update
       * filters without doing a full page reload.
       */
      this.urlState = this.getUrlState();

      this.resultClickHandler =
        this.handleResultClick.bind(this);

      this.loadMoreClickHandler =
        this.handleLoadMoreClick.bind(this);

      this.init();

      document.addEventListener(
        'on:bfcache:load-restore',
        this.reload.bind(this)
      );
    }

    disconnectedCallback() {
      this.destroy();
    }

    /*
     * ============================================================
     * URL / FILTER STATE
     * ============================================================
     */

    getUrl() {
      return new URL(
        window.location.href
      );
    }

    getUrlState() {
      const url = this.getUrl();

      /*
       * Do NOT remove or alter any filters.
       *
       * The complete query string is retained.
       */
      return url.searchParams.toString();
    }

    getCurrentSort() {
      const url = this.getUrl();

      return (
        url.searchParams.get('sort_by') ||
        ''
      );
    }

    isStockPriorityActive() {
      return (
        this.getCurrentSort() ===
        this.stockPrioritySort
      );
    }

    hasUrlStateChanged() {
      return (
        this.getUrlState() !==
        this.urlState
      );
    }

    resetForCurrentUrl() {
      this.urlState =
        this.getUrlState();

      this.stockPage = 1;
      this.outOfStockPage = 1;

      this.inStockFinished = false;
      this.outOfStockFinished = false;

      /*
       * Cancel anything belonging to the
       * previous filter state.
       */
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    /*
     * ============================================================
     * INIT
     * ============================================================
     */

    init() {
      this.infiniteScroll =
        this.dataset.infiniteScroll === 'true';

      this.pagination =
        this.querySelector(
          '.js-pagination'
        );

      this.loadMoreButton =
        this.querySelector(
          '.js-pagination-load-more'
        );

      this.results =
        this.querySelectorAll(
          '.js-pagination-result'
        );

      this.addEventListener(
        'click',
        this.resultClickHandler
      );

      /*
       * Stock priority only applies to Best Selling.
       *
       * Crucially, this is checked against the CURRENT
       * browser URL rather than permanently trusting
       * the original data attribute.
       */
      if (this.isStockPriorityActive()) {
        this.initStockPriority();
      } else {
        this.initNormalPagination();
      }
    }

    /*
     * ============================================================
     * NORMAL PAGINATION
     * ============================================================
     */

    initNormalPagination() {
      if (
        this.pagination &&
        this.pagination.dataset.isMoreResults &&
        this.results
      ) {
        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener(
            'click',
            this.loadMoreClickHandler
          );
        }

        if (this.infiniteScroll) {
          this.createPaginationObserver();
        }
      }
    }

    createPaginationObserver() {
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
      }

      if (!this.pagination) {
        return;
      }

      this.paginationObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach(
              (entry) => {
                if (
                  entry.isIntersecting
                ) {
                  this.loadMore();

                  if (
                    !this.intersectingTimer
                  ) {
                    this.intersectingTimer =
                      setInterval(
                        () => {
                          this.loadMore();
                        },
                        1000
                      );
                  }
                } else if (
                  this.intersectingTimer
                ) {
                  clearInterval(
                    this.intersectingTimer
                  );

                  this.intersectingTimer =
                    null;
                }
              }
            );
          },
          {
            rootMargin:
              `${window.innerHeight * 1.5}px 0px`
          }
        );

      this.paginationObserver.observe(
        this.pagination
      );
    }

    /*
     * ============================================================
     * STOCK PRIORITY
     * ============================================================
     */

    async initStockPriority() {
      try {
        /*
         * Make absolutely sure the current URL has not
         * changed while the component was initialising.
         */
        if (!this.isStockPriorityActive()) {
          this.initNormalPagination();
          return;
        }

        await this.replaceInitialResultsWithInStock();

        /*
         * If the filters changed while the request was
         * running, do not initialise pagination using
         * stale results.
         */
        if (
          this.hasUrlStateChanged()
        ) {
          return;
        }

        this.setupStockPaginationObserver();

      } catch (error) {
        if (
          error &&
          error.name === 'AbortError'
        ) {
          return;
        }

        console.warn(
          'Stock-priority pagination could not initialise.',
          error
        );

        this.initNormalPagination();
      }
    }

    /*
     * ============================================================
     * BUILD STOCK URL
     * ============================================================
     *
     * THIS IS THE IMPORTANT PART.
     *
     * Start with the CURRENT browser URL.
     *
     * Therefore:
     *
     * sort_by
     * filter.p.m.custom.rangemaster
     * filter.p.m.custom.range_width
     * filter.p.m.custom.energy_efficiency_class
     * any other filters
     * vendor
     * type
     * tags
     * price filters
     * etc.
     *
     * are ALL preserved.
     *
     * We ONLY modify:
     *
     * filter.v.availability
     * page
     */

    buildStockUrl(
      stockValue,
      page = 1
    ) {
      const url =
        this.getUrl();

      /*
       * Keep the user's current sort.
       *
       * Do NOT force best-selling here.
       */
      const currentSort =
        url.searchParams.get(
          'sort_by'
        );

      if (currentSort) {
        url.searchParams.set(
          'sort_by',
          currentSort
        );
      }

      /*
       * Add the temporary availability filter.
       *
       * IMPORTANT:
       * This does NOT remove any existing filters.
       */
      url.searchParams.set(
        'filter.v.availability',
        String(stockValue)
      );

      /*
       * Shopify pagination must start at page 1
       * for our first stock request.
       */
      url.searchParams.delete(
        'page'
      );

      if (page > 1) {
        url.searchParams.set(
          'page',
          String(page)
        );
      }

      return url.toString();
    }

    /*
     * ============================================================
     * FETCH
     * ============================================================
     */

    async fetchStockPage(
      stockValue,
      page
    ) {
      const url =
        this.buildStockUrl(
          stockValue,
          page
        );

      /*
       * Capture the URL state BEFORE fetching.
       */
      const requestState =
        this.getUrlState();

      const response =
        await fetch(
          url,
          {
            credentials:
              'same-origin',
            headers: {
              Accept:
                'text/html'
            },
            signal:
              this.controller
                ? this.controller.signal
                : undefined
          }
        );

      if (!response.ok) {
        throw new Error(
          `Stock-priority request failed: ${response.status}`
        );
      }

      const html =
        await response.text();

      /*
       * If the user changed filters while
       * Shopify was responding, discard this
       * response completely.
       */
      if (
        requestState !==
        this.getUrlState()
      ) {
        return null;
      }

      const template =
        document.createElement(
          'template'
        );

      template.innerHTML =
        html;

      return template;
    }

    /*
     * ============================================================
     * INITIAL STOCK RESULTS
     * ============================================================
     */

    async replaceInitialResultsWithInStock() {
      this.isFetching = true;

      this.controller =
        new AbortController();

      try {
        /*
         * IMPORTANT:
         *
         * Use availability = 1,
         * but retain ALL current filters.
         */
        const template =
          await this.fetchStockPage(
            1,
            1
          );

        if (!template) {
          return;
        }

        const productResults =
          this.getProductResults(
            template
          );

        const grid =
          this.querySelector(
            '#filter-results ul.grid'
          );

        if (!grid) {
          throw new Error(
            'Collection product grid could not be found.'
          );
        }

        const productOnly =
          productResults.filter(
            (result) =>
              this.isProductResult(
                result
              )
          );

        /*
         * If there are no in-stock products,
         * move directly to the out-of-stock phase.
         */
        if (
          productOnly.length === 0
        ) {
          this.inStockFinished =
            true;
        }

        /*
         * Remove ONLY existing product results.
         *
         * Promotions are retained.
         */
        this.querySelectorAll(
          'li.js-pagination-result'
        ).forEach(
          (result) => {
            if (
              this.isProductResult(
                result
              )
            ) {
              result.remove();
            }
          }
        );

        productOnly.forEach(
          (result) => {
            grid.insertAdjacentHTML(
              'beforeend',
              result.outerHTML
            );
          }
        );

        this.stockPage = 1;

        const newPagination =
          this.getPagination(
            template
          );

        if (
          !newPagination ||
          newPagination.dataset
            .isMoreResults !==
            'true'
        ) {
          this.inStockFinished =
            true;
        }

        this.updatePaginationState(
          newPagination
        );

        this.results =
          this.querySelectorAll(
            '.js-pagination-result'
          );

        /*
         * DO NOT modify the browser URL.
         *
         * This is deliberately removed.
         *
         * The customer's filters remain exactly
         * as they are.
         */

      } finally {
        this.controller =
          null;

        this.isFetching =
          false;
      }
    }

    /*
     * ============================================================
     * PRODUCT DETECTION
     * ============================================================
     */

    isProductResult(result) {
      if (!result) {
        return false;
      }

      return Boolean(
        result.querySelector(
          '[data-product-card]'
        ) ||
        result.querySelector(
          '.product-card'
        ) ||
        result.querySelector(
          '.card'
        ) ||
        result.querySelector(
          'a[href*="/products/"]'
        )
      );
    }

    /*
     * ============================================================
     * RESULTS / PAGINATION
     * ============================================================
     */

    getProductResults(
      template
    ) {
      if (!template) {
        return [];
      }

      return Array.from(
        template.content.querySelectorAll(
          'li.js-pagination-result'
        )
      );
    }

    getPagination(
      template
    ) {
      if (!template) {
        return null;
      }

      return template.content.querySelector(
        '.js-pagination'
      );
    }

    setupStockPaginationObserver() {
      this.pagination =
        this.querySelector(
          '.js-pagination'
        );

      this.loadMoreButton =
        this.querySelector(
          '.js-pagination-load-more'
        );

      if (!this.pagination) {
        return;
      }

      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener(
          'click',
          this.loadMoreClickHandler
        );

        this.loadMoreButton.addEventListener(
          'click',
          this.loadMoreClickHandler
        );
      }

      if (this.infiniteScroll) {
        this.createPaginationObserver();
      }
    }

    /*
     * ============================================================
     * LOAD MORE
     * ============================================================
     */

    async loadMore() {
      /*
       * Check the CURRENT URL before doing anything.
       *
       * This is what prevents the old filter state
       * from continuing after filters change.
       */
      if (
        this.hasUrlStateChanged()
      ) {
        this.resetForCurrentUrl();

        /*
         * If the new sort is no longer best-selling,
         * completely stop stock-priority behaviour.
         */
        if (
          !this.isStockPriorityActive()
        ) {
          this.removePagination();

          return;
        }

        /*
         * If the filters changed but we're still
         * on best-selling, reinitialise using the
         * NEW filter state.
         */
        await this.initStockPriority();

        return;
      }

      /*
       * Never stock-prioritise another sort.
       */
      if (
        !this.isStockPriorityActive()
      ) {
        return;
      }

      if (this.isFetching) {
        return;
      }

      if (
        !this.inStockFinished ||
        !this.outOfStockFinished
      ) {
        await this.loadStockPriorityMore();

        return;
      }

      this.removePagination();
    }

    /*
     * ============================================================
     * STOCK PRIORITY LOAD MORE
     * ============================================================
     */

    async loadStockPriorityMore() {
      /*
       * Re-check filters immediately before
       * making the request.
       */
      if (
        !this.isStockPriorityActive()
      ) {
        return;
      }

      if (
        this.hasUrlStateChanged()
      ) {
        this.resetForCurrentUrl();

        return;
      }

      this.isFetching =
        true;

      this.controller =
        new AbortController();

      this.setLoading(
        true
      );

      try {
        /*
         * ========================================================
         * PHASE 1 — IN STOCK
         * ========================================================
         */

        if (
          !this.inStockFinished
        ) {
          this.stockPage += 1;

          const template =
            await this.fetchStockPage(
              1,
              this.stockPage
            );

          if (!template) {
            return;
          }

          /*
           * Filter state may have changed while
           * the request was running.
           */
          if (
            this.hasUrlStateChanged()
          ) {
            this.resetForCurrentUrl();

            return;
          }

          const products =
            this.getProductResults(
              template
            );

          this.appendProducts(
            products
          );

          const pagination =
            this.getPagination(
              template
            );

          if (
            !pagination ||
            pagination.dataset
              .isMoreResults !==
              'true'
          ) {
            this.inStockFinished =
              true;
          }

          this.updatePaginationState(
            pagination
          );

          /*
           * If there are still in-stock pages,
           * don't start out-of-stock yet.
           */
          if (
            !this.inStockFinished
          ) {
            return;
          }
        }

        /*
         * ========================================================
         * PHASE 2 — OUT OF STOCK
         * ========================================================
         */

        if (
          this.inStockFinished &&
          !this.outOfStockFinished
        ) {
          this.outOfStockPage += 1;

          const template =
            await this.fetchStockPage(
              0,
              this.outOfStockPage
            );

          if (!template) {
            return;
          }

          if (
            this.hasUrlStateChanged()
          ) {
            this.resetForCurrentUrl();

            return;
          }

          const products =
            this.getProductResults(
              template
            );

          this.appendProducts(
            products
          );

          const pagination =
            this.getPagination(
              template
            );

          if (
            !pagination ||
            pagination.dataset
              .isMoreResults !==
              'true'
          ) {
            this.outOfStockFinished =
              true;
          }

          this.updatePaginationState(
            pagination
          );
        }

        /*
         * Both phases are complete.
         */
        if (
          this.inStockFinished &&
          this.outOfStockFinished
        ) {
          this.removePagination();
        }

      } catch (error) {
        if (
          error &&
          error.name !==
            'AbortError'
        ) {
          console.warn(
            'Unable to fetch stock-priority products.',
            error
          );
        }
      } finally {
        this.controller =
          null;

        this.isFetching =
          false;

        this.setLoading(
          false
        );
      }
    }

    /*
     * ============================================================
     * APPEND PRODUCTS
     * ============================================================
     */

    appendProducts(
      products
    ) {
      if (
        !products ||
        !products.length
      ) {
        return;
      }

      const grid =
        this.querySelector(
          '#filter-results ul.grid'
        );

      if (!grid) {
        return;
      }

      products.forEach(
        (result) => {
          if (
            !this.isProductResult(
              result
            )
          ) {
            return;
          }

          const productLink =
            result.querySelector(
              'a[href*="/products/"]'
            );

          /*
           * Prevent duplicate products.
           */
          if (
            productLink &&
            productLink.href
          ) {
            const existing =
              Array.from(
                grid.querySelectorAll(
                  'a[href*="/products/"]'
                )
              ).some(
                (link) =>
                  link.href ===
                  productLink.href
              );

            if (existing) {
              return;
            }
          }

          /*
           * Insert the COMPLETE product card.
           *
           * Therefore:
           * - metafields
           * - short descriptions
           * - energy efficiency images
           * - badges
           * - prices
           * - availability
           *
           * all remain intact.
           */
          grid.insertAdjacentHTML(
            'beforeend',
            result.outerHTML
          );
        }
      );

      this.results =
        this.querySelectorAll(
          '.js-pagination-result'
        );
    }

    /*
     * ============================================================
     * PAGINATION STATE
     * ============================================================
     */

    updatePaginationState(
      newPagination
    ) {
      this.pagination =
        this.querySelector(
          '.js-pagination'
        );

      if (!this.pagination) {
        return;
      }

      if (
        newPagination &&
        newPagination.dataset
          .isMoreResults ===
          'true'
      ) {
        this.pagination.innerHTML =
          newPagination.innerHTML;

        this.loadMoreButton =
          this.querySelector(
            '.js-pagination-load-more'
          );

        if (
          this.loadMoreButton
        ) {
          this.loadMoreButton.removeEventListener(
            'click',
            this.loadMoreClickHandler
          );

          this.loadMoreButton.addEventListener(
            'click',
            this.loadMoreClickHandler
          );
        }
      }
    }

    /*
     * ============================================================
     * LOADING
     * ============================================================
     */

    setLoading(
      isLoading
    ) {
      this.loadMoreButton =
        this.querySelector(
          '.js-pagination-load-more'
        );

      if (
        this.loadMoreButton
      ) {
        if (isLoading) {
          this.loadMoreButton.classList.add(
            'is-loading'
          );

          this.loadMoreButton.setAttribute(
            'aria-disabled',
            'true'
          );
        } else {
          this.loadMoreButton.classList.remove(
            'is-loading'
          );

          this.loadMoreButton.removeAttribute(
            'aria-disabled'
          );
        }
      }

      if (
        this.pagination
      ) {
        if (isLoading) {
          this.pagination.classList.add(
            'is-loading'
          );
        } else {
          this.pagination.classList.remove(
            'is-loading'
          );
        }
      }
    }

    /*
     * ============================================================
     * REMOVE PAGINATION
     * ============================================================
     */

    removePagination() {
      if (
        this.intersectingTimer
      ) {
        clearInterval(
          this.intersectingTimer
        );

        this.intersectingTimer =
          null;
      }

      if (
        this.paginationObserver
      ) {
        this.paginationObserver.disconnect();

        this.paginationObserver =
          null;
      }

      if (
        this.pagination
      ) {
        this.pagination.remove();

        this.pagination =
          null;
      }

      document
        .querySelectorAll(
          '.js-when-paginated-only'
        )
        .forEach(
          (elem) =>
            elem.remove()
        );
    }

    /*
     * ============================================================
     * EVENTS
     * ============================================================
     */

    handleLoadMoreClick(
      evt
    ) {
      evt.preventDefault();

      this.loadMore();
    }

    handleResultClick(
      evt
    ) {
      const link =
        evt.target.closest(
          'a'
        );

      const result =
        evt.target.closest(
          '.js-pagination-result'
        );

      if (
        link &&
        result &&
        this.controller
      ) {
        this.controller.abort();
      }
    }

    /*
     * ============================================================
     * DESTROY
     * ============================================================
     */

    destroy() {
      if (
        this.intersectingTimer
      ) {
        clearInterval(
          this.intersectingTimer
        );

        this.intersectingTimer =
          null;
      }

      if (
        this.paginationObserver
      ) {
        this.paginationObserver.disconnect();

        this.paginationObserver =
          null;
      }

      if (
        this.loadMoreButton
      ) {
        this.loadMoreButton.removeEventListener(
          'click',
          this.loadMoreClickHandler
        );
      }

      this.removeEventListener(
        'click',
        this.resultClickHandler
      );

      if (
        this.controller
      ) {
        this.controller.abort();

        this.controller =
          null;
      }
    }

    /*
     * ============================================================
     * BFCACHE RELOAD
     * ============================================================
     */

    reload() {
      this.destroy();

      this.stockPage = 1;
      this.outOfStockPage = 1;

      this.inStockFinished =
        false;

      this.outOfStockFinished =
        false;

      this.isFetching =
        false;

      /*
       * Take a fresh snapshot of the
       * CURRENT URL.
       */
      this.urlState =
        this.getUrlState();

      this.init();
    }
  }

  customElements.define(
    'custom-pagination',
    CustomPagination
  );
}